import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { verifyMemberTrackerPasswordApi } from "src/DAL/tracker/tracker";
import { PRIMARY } from "./memberTrackersStorage";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";

/** True when a member journey API response means "ask for the password". */
export const isJourneyPasswordRequired = (res) =>
  res?.code === 403 && res?.requirePassword === true;

/** Small "Password protected" chip for journey cards. */
export const JourneyLockChip = () => (
  <Chip
    size="small"
    icon={<LockOutlinedIcon sx={{ fontSize: "14px !important" }} />}
    label="Password protected"
    sx={{
      height: 24,
      fontWeight: 700,
      fontSize: 11.5,
      bgcolor: "#f1f5f9",
      color: MUTED,
      "& .MuiChip-icon": { color: MUTED },
    }}
  />
);

/**
 * Password prompt for a protected journey (detail + check-in pages).
 * Calls onUnlocked() after the backend accepts the password.
 */
export default function JourneyPasswordGate({ idOrSlug, tracker, onUnlocked }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await verifyMemberTrackerPasswordApi(idOrSlug, { password });
      if (res?.code === 200) {
        onUnlocked?.();
        return;
      }
      setError(res?.message || "Could not verify the password. Please try again.");
    } catch (_err) {
      setError("Could not verify the password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2.5,
          cursor: "pointer",
          width: "fit-content",
          color: MUTED,
          "&:hover": { color: PRIMARY },
        }}
        onClick={() => navigate("/trackers")}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 650 }}>
          All journeys
        </Typography>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          maxWidth: 440,
          border: `1px solid ${BORDER}`,
          borderRadius: "14px",
          bgcolor: "#fff",
          p: 3,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "11px",
            bgcolor: "color-mix(in srgb, var(--portal-theme-primary) 12%, white)",
            color: PRIMARY,
            display: "grid",
            placeItems: "center",
            mb: 1.5,
          }}
        >
          <LockOutlinedIcon fontSize="small" />
        </Box>
        {tracker?.name ? (
          <h2 style={{ margin: "0 0 6px" }}>{tracker.name}</h2>
        ) : null}
        <Typography sx={{ color: MUTED, fontSize: 14.5, lineHeight: 1.5, mb: 2.5 }}>
          This journey is password protected. Enter the password from your
          coach to continue.
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="password"
          label="Password"
          autoFocus
          autoComplete="current-password"
          value={password}
          disabled={submitting}
          error={Boolean(error)}
          onChange={(e) => {
            setPassword(e.target.value);
            if (error) setError("");
          }}
        />
        {error ? (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        ) : null}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
          <button
            type="submit"
            className="small-contained-button"
            disabled={!password || submitting}
            style={{
              opacity: !password || submitting ? 0.55 : 1,
              minWidth: 120,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {submitting ? <CircularProgress size={14} color="inherit" /> : null}
            {submitting ? "Unlocking…" : "Unlock"}
          </button>
        </Box>
      </Box>
    </div>
  );
}
